import { useRef, useState } from 'react';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import Router from 'next/router';
import api from 'api';
import { availableCustomAttributesByType } from 'lib/customAttributes';
import { Avatar, FlexContainer, ModalButtonWithForm } from 'components/common';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import { FormGroup, RadioGroup, Text } from 'components/common/form';
import { AchievementImportJson } from '.';

import type { Achievement } from 'shared-types';

type FormValues = Pick<Achievement, 'customAttributes' | 'title' | 'type'>;

interface Props {
  updateData: () => void;
}

const AchievementsCreate = ({ updateData }: Props) => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const titleInputContainer = useRef<HTMLInputElement>(undefined);

  const [achievementType, setAchievementType] =
    useState<Achievement['type']>('external');

  const handleModalVisible = () => {
    if (!titleInputContainer.current) return;
    titleInputContainer.current.querySelector('input').focus();
  };

  const handleTypeChange = (value: Achievement['type']) => {
    setAchievementType(value);
  };

  const submit = async (values: FormValues) => {
    try {
      const response = await api.post('/admin/achievements', {
        customAttributes: values.customAttributes || {},
        title: values.title?.trim(),
        type: values.type
      });
      dispatch.addNotice({
        text: messages.achievementsCreateAchievementSuccess,
        type: 'success'
      });
      Router.push(
        '/admin/achievements/[achievementId]/settings',
        `/admin/achievements/${response.data.achievement.id}/settings`
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = async (value: FormValues['title']) => {
    if (!value?.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/achievements?title=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  const initialValues = {
    customAttributes: {},
    title: '',
    type: 'external'
  } as FormValues;

  const customAttributes =
    availableCustomAttributesByType({ serverConfig, type: achievementType }) ||
    [];

  const isVuoadmin = user?.roles.includes('vuoadmin');

  return (
    <FlexContainer>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="plus"
        buttonText={messages.achievementsCreateAchievement}
        initialValues={initialValues}
        modalActionText={messages.commonCreate}
        modalTitle={messages.achievementsCreateAchievement}
        onModalVisible={handleModalVisible}
        onSubmit={submit}
        pristineIsValid={false}
        {...(isVuoadmin
          ? {
              buttonStyle: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                marginRight: 1
              }
            }
          : {})}
      >
        {() => (
          <>
            <FormGroup
              htmlFor="title"
              label={messages.achievementTitle}
              labelHelpTextServerConfigKey="achievementTitle"
            >
              <Text
                customValidateAsync={validateTitle}
                field="title"
                id="title"
                inputContainerRef={(el: HTMLInputElement) =>
                  (titleInputContainer.current = el)
                }
                required={true}
              />
            </FormGroup>

            <FormGroup label={messages.commonType} style={{ marginTop: 25 }}>
              <RadioGroup
                id="achievement-type"
                field="type"
                onChange={handleTypeChange}
                options={Object.keys(achievementTypes).map((type) => ({
                  id: `achievement-type-${type}`,
                  label: (
                    <div>
                      <Avatar
                        fontSize="1.5rem"
                        icon={achievementTypes[type].icon}
                        padding="8px 0"
                        size="34px"
                        style={{ margin: '0 10px 0 5px' }}
                      />
                      <span
                        style={{
                          fontWeight: 600,
                          position: 'relative',
                          top: 2
                        }}
                      >
                        {achievementTypes[type].title}
                      </span>
                    </div>
                  ),
                  value: type
                }))}
                spacing="10px"
                styleAsCards={true}
              />
            </FormGroup>

            {customAttributes?.some(
              (attr) => attr.required || attr.displayInCreateForm
            ) ? (
              <div style={{ marginTop: 30 }}>
                <ProductCustomAttributes
                  hideWarnings={true}
                  renderOnlyRequiredOrDisplayable={true}
                  type={achievementType}
                />
              </div>
            ) : null}
          </>
        )}
      </ModalButtonWithForm>

      {isVuoadmin ? (
        <AchievementImportJson
          createAchievement={true}
          updateData={updateData}
        />
      ) : null}
    </FlexContainer>
  );
};

export default AchievementsCreate;
